import PropTypes from "prop-types";
import { Dialog, DialogContent, Icon, Radio, useMediaQuery } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";

function CategoriaVoucherSelectorDialog({ open, categorias, selectedId, onCancel, onConfirm }) {
  const fullScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [search, setSearch] = useState("");
  const [pendingId, setPendingId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setPendingId(Number(selectedId || 0) || null);
  }, [open, selectedId]);

  const categoriasFiltradas = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("es");
    if (!term) return categorias;
    return categorias.filter((categoria) =>
      String(categoria?.categoria || "").toLocaleLowerCase("es").includes(term)
    );
  }, [categorias, search]);

  const handleConfirm = () => {
    const categoria = categorias.find((item) => Number(item.id) === Number(pendingId));
    if (categoria) onConfirm(categoria);
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth={false}
      fullScreen={fullScreen}
      aria-labelledby="categoria-voucher-selector-title"
      PaperProps={{
        sx: {
          width: fullScreen ? "100%" : "fit-content",
          maxWidth: fullScreen ? "100%" : "calc(100vw - 64px)",
          m: fullScreen ? 0 : 2,
        },
      }}
    >
      <DialogContent
        sx={{
          width: { xs: "auto", sm: "max-content" },
          maxWidth: "100%",
          p: { xs: 2, sm: 3 },
        }}
      >
        <SoftBox display="flex" justifyContent="space-between" alignItems="start" mb={2}>
          <SoftBox>
            <SoftTypography id="categoria-voucher-selector-title" variant="h5" fontWeight="bold">
              Seleccionar categoría
            </SoftTypography>
            <SoftTypography variant="caption" color="secondary">
              El voucher sólo podrá aplicarse a productos de esta categoría.
            </SoftTypography>
          </SoftBox>
          <SoftButton color="primary" size="small" circular iconOnly onClick={onCancel} aria-label="Cerrar selector de categorías">
            <Icon>close</Icon>
          </SoftButton>
        </SoftBox>

        <SoftInput
          id="categoria-voucher-search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Buscar categoría"
          icon={{ component: "search", direction: "left" }}
          inputProps={{ "aria-label": "Buscar categoría" }}
        />

        <SoftBox mt={2} width="100%" sx={{ border: "1px solid rgba(148,163,184,.24)", borderRadius: "14px", overflow: "hidden" }}>
          <SoftBox
            display="grid"
            sx={{
              gridTemplateColumns: "52px minmax(0, 1fr)",
              pl: 0.75,
              backgroundColor: "#f8fafc",
              borderBottom: "1px solid rgba(148,163,184,.2)",
            }}
          >
            <SoftBox p={1} />
            <SoftTypography variant="caption" fontWeight="bold" color="secondary" p={1.25}>
              Categoría
            </SoftTypography>
          </SoftBox>
          <SoftBox sx={{ maxHeight: fullScreen ? "calc(100vh - 270px)" : 360, overflowY: "auto" }}>
            {categoriasFiltradas.length ? categoriasFiltradas.map((categoria) => {
              const checked = Number(pendingId) === Number(categoria.id);
              return (
                <SoftBox
                  key={categoria.id}
                  component="button"
                  type="button"
                  onClick={() => setPendingId(Number(categoria.id))}
                  display="grid"
                  alignItems="center"
                  width="100%"
                  textAlign="left"
                  sx={{
                    gridTemplateColumns: "52px minmax(0, 1fr)",
                    pl: 0.75,
                    border: 0,
                    borderBottom: "1px solid rgba(148,163,184,.16)",
                    backgroundColor: checked ? "rgba(37,99,235,.07)" : "#fff",
                    cursor: "pointer",
                    transition: "background-color 180ms ease",
                    "&:hover": { backgroundColor: checked ? "rgba(37,99,235,.1)" : "#f8fafc" },
                    "&:focus-visible": { outline: "2px solid #2563eb", outlineOffset: -2 },
                    "&:last-of-type": { borderBottom: 0 },
                  }}
                >
                  <Radio checked={checked} tabIndex={-1} inputProps={{ "aria-hidden": true }} />
                  <SoftTypography
                    variant="button"
                    fontWeight={checked ? "bold" : "regular"}
                    p={1.25}
                    sx={{ whiteSpace: { xs: "normal", sm: "nowrap" } }}
                  >
                    {categoria.categoria}
                  </SoftTypography>
                </SoftBox>
              );
            }) : (
              <SoftBox minHeight="180px" display="grid" sx={{ placeItems: "center" }} p={2}>
                <SoftTypography variant="button" color="secondary">No se encontraron categorías.</SoftTypography>
              </SoftBox>
            )}
          </SoftBox>
        </SoftBox>

        <SoftBox mt={2.5} display="flex" justifyContent="space-between" gap={1}>
          <SoftButton color="secondary" size="small" circular onClick={onCancel}>
            Cancelar
          </SoftButton>
          <SoftButton color="primary" size="small" circular disabled={!pendingId} onClick={handleConfirm}>
            Seleccionar
          </SoftButton>
        </SoftBox>
      </DialogContent>
    </Dialog>
  );
}

CategoriaVoucherSelectorDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  categorias: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired, categoria: PropTypes.string })).isRequired,
  selectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

CategoriaVoucherSelectorDialog.defaultProps = { selectedId: null };

export default CategoriaVoucherSelectorDialog;
